import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import api from '@/admin/lib/api'
import { invalidateClaimsQueries } from '@/admin/hooks/use-claims'
import type { AddressValue } from '@/shared/components/ui/address-input'

// ── Types ────────────────────────────────────────────────

export interface SettlementLineItem {
  uuid: string
  category: 'MATERIAL' | 'LABOR' | 'OTHER'
  description: string
  product_line: string
  product_code: string
  work_type: string
  quantity: number
  unit_price: number
  extended_price: number
  ledger_account: string
  /**
   * Semantic intent for the assigned ledger account (e.g. 'reserve',
   * 'goodwill'). Surfaced by the backend from
   * ``LedgerRoutingRule.result_metadata`` so the frontend renders
   * badge variants from intent instead of hardcoded GL numbers.
   * Phase 6 of the tenant-agnostic claims migration.
   */
  ledger_intent?: string
  ledger_override: boolean
  ledger_override_reason: string
  profit_centre: string
  company_code: string
  notes: string
  order: number
  is_locked: boolean
  created_at: string
  updated_at: string
  item_detail?: string | null
  payment_type?: string | null
  uom?: string | null
  ledger_type?: string | null
  payee?: string | null
  /** Payee remittance address (composite ADDRESS value), when captured. */
  payee_address?: AddressValue | null
  payment_date?: string | null
  recipient_type?: string | null
  sap_short_code?: string | null
  product_name?: string | null
  color?: string | null
  /**
   * Per-row coverage determination for a multi-item (e.g. multi-hose)
   * settlement worked out on the parent claim: fully / partially / not
   * covered / goodwill. EAV-sourced; only present for tenants that have
   * seeded the ``coverage_decision`` attribute on ``settlement_line_item``.
   */
  coverage_decision?: string | null
  /**
   * The share of the line paid as a voucher rather than against the full
   * quantity. EAV-sourced; absent for tenants that have not seeded it.
   */
  voucher_quantity?: string | number | null
  /**
   * WPS-175 §8 labour uplift. ``labor_system_rate`` is the published geozone
   * rate the line started from, ``additional_amount`` what was added on top,
   * and ``total_labor_rate`` the sum the line is actually settled at (which
   * is also its ``unit_price``). ``reason_for_increase`` is mandatory once an
   * additional amount is entered.
   */
  labor_system_rate?: string | number | null
  additional_amount?: string | number | null
  total_labor_rate?: string | number | null
  reason_for_increase?: string | null
  /**
   * For a multi-item (umbrella) settlement row, the UUID of the child claim
   * (e.g. the hose) this row represents - used to fetch that hose's evidence
   * photos. Null for ordinary, hand-authored line items.
   */
  source_child_claim?: string | null
}

export interface ClaimPayment {
  uuid: string
  payment_type: string
  /**
   * WPS-175 #15: the line items this payment settles, so the Process Payment
   * popup can identify it by what it pays for. Recipient and payee are
   * optional and unset on an auto-materialised payment; these are not.
   */
  covers?: { label: string; amount: string }[]
  /**
   * Legacy platform key (`PAY-...`) on historic rows. NOT what the Reference
   * column shows - see `settlement_reference` (WPS-175 #10).
   */
  reference_number: string
  /**
   * The check / credit number the payment actually settled under, from SAP,
   * the automated check download, or manual entry. Empty string until the
   * payment is processed with one.
   */
  settlement_reference: string
  /** True once a reference exists; manual entry is closed from then on. */
  reference_is_locked: boolean
  /** A number typed onto the row before it was paid; not a reference yet. */
  held_reference?: string
  /** False while money may not move yet, e.g. its voucher is not redeemed. */
  processable?: boolean
  /** Why a hold stops this payment, in words; empty when nothing holds it. */
  blocked_message?: string
  /** The provider's reason a failed payment was not paid. */
  failure_reason?: string
  /**
   * WPS-244: an instrument released it (a redeemed voucher), so it can be
   * processed whatever stage the claim is in, closed included.
   */
  released?: boolean
  external_reference: string
  /**
   * The provider that pays this row out, resolved from the method when the row
   * was created. Empty means a person settles it, so the client asks for a
   * reference; a provider mints its own and there is nothing to ask.
   */
  disbursement_provider?: string
  amount: number
  payment_date: string | null
  /** Date the payment posted. Null until the payment is processed. */
  post_date: string | null
  processed_at: string | null
  note: string
  status: string
  /** WPS-165 #14: why the Reference field is locked, in words - where the
   *  number came from, and for a hand-entry who supplied it and when. Empty
   *  while no reference exists. */
  reference_origin?: string
  /** 'SYNCED' | 'MANUAL' | '' - the raw provenance behind `reference_origin`,
   *  for choosing an icon rather than for display. */
  reference_source?: string
  /** Human label for `status` from the model's own choices - e.g.
   *  'Issued' rather than SUBMITTED_TO_EXTERNAL. */
  status_label?: string
  /** Cancelled after it was paid. */
  is_voided?: boolean
  /** Whether it can be voided now (paid, and not paid by voucher). */
  voidable?: boolean
  /** Reference of the voided payment this one was reissued for. */
  replaces_reference?: string
  /** On a voided row, the payment reissued for it. */
  replaced_by?: {
    uuid: string
    /** Blank until the replacement has a cheque or credit number. */
    reference: string
    amount: string
    status_label: string
  } | null
  /** Why, when and by whom it was cancelled or voided. */
  cancellation_reason?: string
  cancelled_at?: string | null
  cancelled_by_name?: string
  order: number
  created_at: string
  updated_at: string
  recipient_type?: string | null
  payee?: string | null
}

export interface ItemDetailSubtotal {
  item_detail: string
  label: string
  subtotal: number
}

/** One ledger's settlement subtotal, with its per-claim cap when capped. */
export interface LedgerSubtotal {
  ledger_type: string
  label: string
  subtotal: number
  cap_amount?: number | null
  is_goodwill: boolean
  triggers_loa: boolean
}

export interface LineProration {
  category: string
  quantity: number | string | null
  amount: number | string
  /** A rounded-up line's quantity before rounding, and the line it is (WPS-242). */
  calculated_quantity?: number | string | null
  description?: string
}

export interface SettlementSummary {
  // WPS-165 #12: OC material only. Non-OC material is reported separately and
  // is not prorated against the OC warranty.
  material_subtotal: number
  non_oc_material_subtotal: number
  labor_subtotal: number
  other_subtotal: number
  settlement_total: number
  material_proration_pct: number | null
  labor_proration_pct: number | null
  prorated_material: number | null
  prorated_labor: number | null
  prorated_total: number | null
  /**
   * WPS-165 #12: the payable figure - warranty-prorated OC material and labor
   * plus the Non-OC Material and Other lines that are never prorated. This is
   * what payment calculation and settlement review read. Equals
   * ``settlement_total`` when no proration formula applies.
   */
  payable_total: number
  proration_formula_code?: string | null
  proration_formula_name?: string | null
  proration_formula_scope?: string | null
  /** WPS-242: how the tenant rounds prorated material quantities; null keeps them exact. */
  prorated_quantity_rounding?: 'up' | 'nearest' | null
  /** What each prorated line pays, keyed by line uuid (from the backend). */
  line_prorations?: Record<string, LineProration>
  warranty_ledger_total?: number | null
  goodwill_ledger_total?: number | null
  // WPS-147: the goodwill that still needs leader sign-off (what the Leader
  // Goodwill LOA gates on) - a subset of ``goodwill_ledger_total``.
  leader_goodwill_total?: number | null
  // Per-ledger settlement breakdown (RS / Sales / Legal / Leader goodwill,
  // Warranty, ...) with each ledger's per-claim cap when it carries one.
  ledger_subtotals?: LedgerSubtotal[]
  goodwill_pct?: number | null
  category_totals?: Record<string, number> | null
  // WP1-61: per-Item-Detail subtotals for the summary card + approval dialog.
  item_detail_subtotals?: ItemDetailSubtotal[]
  // WP1-65: one Levels-of-Approval dimension per active cascade path
  // (Warranty / Business Goodwill). Empty when the tenant has no LOA
  // cascade configured for this claim's workflow.
  loas?: LOADimension[]
}

// ── LOA Cascade (Levels of Approval) Types ─────────────────────────────
//
// WP1-65. One dimension per independent approval cascade path (Warranty vs
// Business Goodwill). Distinct from the legacy single-``LOAData`` shape
// above (``approval_tracks`` / ``loa_pending`` / ``ho_approval`` states) -
// this is the generic ``pending_approval`` cascade driven by
// ``apps.workflows.services.approval_policy`` + ``approval_cascade`` and
// read by ``apps.claims.services.loa_panel.build_loa_dimension_states``.
// The approver acts via the existing workflow actions (``approve_payment`` /
// ``return_for_review``) already surfaced in the claim's action menu - this
// panel is a read view of cascade progress only.

export interface LOACascadeTier {
  order: number
  role_label: string
  approver_name: string
  min_amount: string | null
  max_amount: string | null
  /**
   * Optional display override for the band text (WPS-147). A trigger-style
   * ladder - one rung, fires on any overage - reads as its rule ("Any labor
   * beyond the allowed variance") rather than as a "$1.00 - No cap" range.
   */
  band_label?: string
  /**
   * Identity of this rung's approver (WPS-147 §5). Rungs sharing a key are
   * co-signed by a single approval in the engine, so the panel groups them
   * into one decision instead of re-deriving that from display labels.
   */
  approver_key?: string
  /** The role this rung routes to, when it names one. */
  approver_role?: string
  status: 'APPROVED' | 'PENDING' | 'NOT_REQUIRED'
  is_current_tier: boolean
}

// WPS-124: one real per-rung sign-off, reconstructed from the claim's workflow
// action log. ``status`` is APPROVED when the rung was signed off, DENIED when
// the approver returned the claim for review.
export interface LOAApprovalRecord {
  approver_name: string
  role_label: string
  min_amount: string | null
  max_amount: string | null
  // Display override for the band text - a trigger-style or always-required
  // rung reads as its rule, not a dollar range (WPS-147/WPS-165).
  band_label?: string
  status: 'APPROVED' | 'DENIED'
  decided_at: string | null
  notes: string
}

/**
 * The figures an LOA decision rests on, shipped with the LOA payload rather
 * than the settlement block (WPS-147 §5) - the settlement tab is hidden at the
 * approval step, but the approver still has to see the claim total and where
 * the goodwill sits.
 */
export interface LOAContext {
  settlement_total?: string | number | null
  goodwill_pct?: string | number | null
  ledger_subtotals?: LedgerSubtotal[]
}

export interface LOADimension {
  // WPS-147 adds LABOR: the labor-variance trigger renders as its own block.
  // WPS-165 adds LEADERSHIP: the always-required pre-homeowner leadership gate,
  // a second cascade on the OC Installed workflow.
  loa_type: 'WARRANTY' | 'GOODWILL_BUSINESS' | 'LABOR' | 'LEADERSHIP'
  key: 'warranty' | 'goodwill' | 'labor' | 'leadership'
  label: string
  driving_amount: string | null
  loa_status: 'NOT_STARTED' | 'PENDING' | 'APPROVED'
  required_tier: number
  approved_tier: number
  // WPS-175 #14: the workflow state THIS cascade parks a claim in while it
  // waits for sign-off, straight from the seeded cascade config. Compare
  // against it instead of hardcoding a state key: a workflow can carry more
  // than one cascade with different pending states, the keys are tenant
  // config, and a stale constant makes the whole LOA surface silently
  // disappear rather than fail loudly.
  pending_state_key?: string
  /**
   * The actions that RESOLVE this cascade, straight from its config. The LOA
   * decision buttons post these rather than a fixed `loa_approve`, which
   * exists on the roofing workflows and nowhere else - on a workflow whose
   * cascade names the action differently, Approve failed outright.
   */
  approve_action_key?: string
  return_action_key?: string
  /** Empty when this cascade has no Deny (only Request Changes). */
  deny_action_key?: string
  /** "cumulative": every level up to the amount signs; "band": only its level. */
  sign_off_mode?: 'cumulative' | 'band'
  /** What the driving amount is, from the cascade config ("Leader Goodwill"). */
  amount_label?: string
  threshold_tiers: LOACascadeTier[]
  approval_records?: LOAApprovalRecord[]
  /** What the last full sign-off approved. */
  covered_amount?: string | null
  /**
   * The amount is approved by an earlier sign-off rather than one in this
   * round: after a return or a voided payment, an amount at or below what was
   * approved needs no new approval.
   */
  covered_by_earlier_approval?: boolean
  /** Sign-offs from earlier approval rounds, oldest first. */
  approval_history?: (LOAApprovalRecord & { round: number })[]
}

/**
 * Whether the viewer can sign the level the claim is waiting on, and why not.
 * Null when nothing is waiting.
 */
export interface LOAViewerState {
  can_sign: boolean
  reason: '' | 'submitted' | 'not_signer'
  pending_role_label: string
  /** The OC role the waiting level routes to, when it names one. */
  pending_role?: string
  /** Nobody holds the waiting level's role yet, so any approver may sign. */
  role_unfilled: boolean
}

/** A settlement line or payment as an approval decision saw it. */
export type ApprovalChangeRow = { uuid: string } & Record<string, string | null>

export interface ApprovalChange {
  change: 'added' | 'changed' | 'removed'
  /** The fields that moved, on a changed row. */
  fields?: string[]
  before: ApprovalChangeRow | null
  after: ApprovalChangeRow | null
}

/**
 * What moved since the last approval decision, while the claim waits for
 * approval again. Null on a first approval.
 */
export interface ApprovalChanges {
  since: { action_label: string; by: string; at: string }
  amounts: {
    key: string
    label: string
    before: string | null
    after: string | null
  }[]
  lines: ApprovalChange[]
  payments: ApprovalChange[]
}

// ── LOA Types ───────────────────────────────────────────

export type LOAType = 'GOODWILL_BUSINESS' | 'WARRANTY'

export interface LOAThresholdTier {
  role_label: string
  min_amount: number
  max_amount: number | null
  is_current_tier: boolean
  approved_by: string | null
  approved_at: string | null
  status: 'APPROVED' | 'PENDING' | 'NOT_REQUIRED'
}

export interface LOAApprovalTrackDetail {
  track: 'TOTAL_DOLLAR' | 'LABOR_OVERAGE' | 'GOODWILL_PCT'
  label: string
  triggered: boolean
  current_value: number
  threshold_value: number
  status: 'APPROVED' | 'PENDING' | 'NOT_REQUIRED'
}

export interface LOAData {
  loa_type: LOAType | null
  loa_status: 'NOT_STARTED' | 'PENDING' | 'APPROVED' | 'REJECTED'
  approval_tracks: LOAApprovalTrackDetail[]
  threshold_tiers: LOAThresholdTier[]
  can_start_loa: boolean
  start_blocked_reason: string | null
  decision: string | null
  rationale: string | null
  decided_by: string | null
  decided_at: string | null
}

export interface SettlementData {
  line_items: SettlementLineItem[]
  summary: SettlementSummary
  payments: ClaimPayment[]
  visible: boolean
  line_items_editable: boolean
  // WP1-62: the claim has moved past its staged settlement step, so pricing
  // is frozen. Always false on a workflow without a staged settlement.
  prices_locked?: boolean
  payments_editable: boolean
  loa?: LOAData | null
  // WP1-62: when settlement prices were last refreshed (null if never).
  prices_refreshed_at?: string | null
  /**
   * WPS-119: the payment type a NEW line on this claim type starts on, or
   * null when the claim type names none (every other claim type today). Set
   * per claim TYPE in config, not per tenant, so a tenant can default one
   * claim type and leave the rest choosing freely.
   */
  default_payment_type?: string | null
}

/**
 * Phase 7 of the tenant-agnostic claims migration. Backend-driven enum
 * options for the settlement line-item form: ``category`` and
 * ``work_type`` come from generic Django ``TextChoices`` on the
 * settlement / pricing models; ``product_line`` is aggregated from
 * ``MaterialPricingRate`` rows scoped to the active organisation so
 * each tenant gets its own product-line vocabulary without code.
 *
 * PR #392 assimilation (M1+M2): ``item_details``, ``payment_types``,
 * ``ledger_types``, ``company_codes``, ``profit_centres``, and ``uoms``
 * are sourced from ``EntityAttributeDefinition.configuration_data.
 * options[*]`` on the Tier 2 ``settlement_line_item`` / ``claim_payment``
 * entities. Per-option metadata (``kind``, ``category``, ``ledger_intent``,
 * ``badge_variant``, ``allowed_items``) replaces the hardcoded constants
 * the legacy ``settlement-constants.ts`` file used to ship.
 */
export interface SettlementOptionItem {
  value: string
  label: string
  /** Extra per-option metadata (kind, category, ledger_intent, badge_variant, allowed_items, …). */
  [key: string]: unknown
}

/**
 * WPS-162: a product base the Material Pricing Table publishes for the claim's
 * geozone. An OC-Material settlement line selects its product from this list
 * (not the product library) and prices from the same rows - so each option
 * carries the representative rate's line/uom/sap/price. Empty unless the
 * options call is made with a ``?claim=`` whose zone and pricing resolve.
 */
export interface MaterialProductOption {
  value: string
  label: string
  product_line: string
  uom: string
  sap_short_code: string
  price: string
}

export interface SettlementOptions {
  categories: SettlementOptionItem[]
  work_types: SettlementOptionItem[]
  product_lines: SettlementOptionItem[]
  /**
   * WPS-162: product bases from the Material Pricing Table for the claim's
   * geozone. Feeds the OC-Material line's Product Base picker; empty for a
   * non-material item or a claim with no resolvable priced zone.
   */
  material_products: MaterialProductOption[]
  item_details: SettlementOptionItem[]
  payment_types: SettlementOptionItem[]
  ledger_types: SettlementOptionItem[]
  company_codes: SettlementOptionItem[]
  profit_centres: SettlementOptionItem[]
  uoms: SettlementOptionItem[]
  recipient_types: SettlementOptionItem[]
  /**
   * Per-row coverage-decision options (Fully / Partially / Not Covered,
   * Goodwill). Empty for tenants that have not seeded the attribute - the
   * line-item table hides the coverage column in that case.
   */
  coverage_decisions: SettlementOptionItem[]
  /**
   * True once the tenant has published an approved unit of measure for at
   * least one product. The Add Line Item card refuses an unmapped product
   * only when this is true - against an empty mapping table the rule would
   * refuse every product and block settlement entirely (WPS-175 #7).
   */
  uom_mapping_available?: boolean
  /**
   * Why a labour line is paid above the published rate. Empty for tenants
   * that have not seeded the attribute - the form hides the field then.
   */
  reasons_for_increase: SettlementOptionItem[]
  /**
   * ``required_when`` rules authored on line-item fields, keyed by field (e.g.
   * a reason required on every labour line). Empty when a tenant has none.
   */
  required_when?: Record<string, Record<string, unknown[]>>
}

export function useSettlementOptions(claimUuid?: string) {
  // WPS-164 8A/7A: pass the claim so the backend can scope ledger + payment
  // options to that claim type's policy (uninstalled = Goodwill ledgers only,
  // no Voucher). Omitting the claim (e.g. the cross-claim bulk-settle dialog)
  // keeps the full lists. Keyed per claim so scoped lists cache separately.
  return useQuery<SettlementOptions>({
    queryKey: ['settlement-options', claimUuid ?? null],
    queryFn: () =>
      api
        .get(
          '/claims/settlement/options/',
          claimUuid ? { params: { claim: claimUuid } } : undefined,
        )
        .then((r) => r.data),
    staleTime: 5 * 60 * 1000, // config-time data
  })
}

// ── Mutations ────────────────────────────────────────────

export function useCreateLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(`/claims/claims/${claimUuid}/settlement/line-items/`, data)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useUpdateLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      itemUuid,
      data,
    }: {
      itemUuid: string
      data: Record<string, unknown>
    }) =>
      api
        .put(
          `/claims/claims/${claimUuid}/settlement/line-items/${itemUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useDeleteLineItem(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemUuid: string) =>
      api.delete(
        `/claims/claims/${claimUuid}/settlement/line-items/${itemUuid}/`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useReorderLineItems(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedUuids: string[]) =>
      api
        .post(`/claims/claims/${claimUuid}/settlement/line-items/reorder/`, {
          ordered_uuids: orderedUuids,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

// WP1-62: stamp the claim with the time prices were last refreshed.
export function useMarkPricesRefreshed(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .post(`/claims/claims/${claimUuid}/settlement/prices-refreshed/`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

/**
 * The claim's payment ledger.
 *
 * Separate from the settlement summary's ``payments``: that is fed by the
 * settlement working-out and only exists for a tenant running GL line items.
 * This reads the ledger itself, which every tenant with the ``claim_payment``
 * entity has. The endpoint 404s without that entity, so callers gate on it.
 */
export function useClaimPayments(claimUuid: string, enabled = true) {
  return useQuery<ClaimPayment[]>({
    queryKey: ['claim', claimUuid, 'payments'],
    // ``api`` unwraps the {status,message,result} envelope, so r.data IS the
    // list - reading ``.result`` off it yields undefined and an empty table.
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/payments/`)
        .then((r) => (Array.isArray(r.data) ? r.data : [])),
    enabled: enabled && !!claimUuid,
  })
}

export function useCreatePayment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(`/claims/claims/${claimUuid}/payments/`, data)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export interface ProcessPaymentInput {
  paymentUuid: string
  reference_number: string
  /** Date the payment posts, `YYYY-MM-DD`. Optional (WPS-175 #10). */
  payment_date?: string | null
  processed_at?: string | null
  note?: string | null
  /**
   * Set once the operator has read the duplicate-reference warning and
   * chosen to proceed. Absent on the first attempt, so the server always
   * gets to raise the warning before a duplicate can be recorded.
   */
  acknowledge_duplicate?: boolean
}

export function useProcessPayment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentUuid, ...body }: ProcessPaymentInput) =>
      api
        .post(
          `/claims/claims/${claimUuid}/payments/${paymentUuid}/process/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export interface RecordPaymentReferenceInput {
  paymentUuid: string
  reference_number: string
  /** Captured with the reference; required only when the tenant's
   *  `payment_date` attribute is, otherwise null when left blank. */
  payment_date?: string | null
  acknowledge_duplicate?: boolean
}

/** WPS-165 #13/#14: the reference for a payment already made. A cheque
 *  number arrives after the cheque does, so recording the payment and
 *  recording its reference are separate writes. */
export function useRecordPaymentReference(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentUuid, ...body }: RecordPaymentReferenceInput) =>
      api
        .post(
          `/claims/claims/${claimUuid}/payments/${paymentUuid}/reference/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

/** Hand a routed payment to its provider. Nothing to supply - it mints its own reference. */
export function useSubmitPayment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentUuid }: { paymentUuid: string }) =>
      api
        .post(`/claims/claims/${claimUuid}/payments/${paymentUuid}/submit/`, {})
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

// ── Pricing Lookup ──────────────────────────────────────

export interface MaterialPriceResult {
  price: string
  uom: string
  sap_short_code: string
  pricing_source: string
  geozone_name: string
}

export interface LaborRateResult {
  rate_per_square: string
  work_type: string
  geozone_name: string
  /** The unit the rate is denominated in - always squares, since a labour
   *  rate is stored as `rate_per_square` (WPS-175 #8). */
  uom: string
}

export function useMaterialPriceLookup(claimUuid: string) {
  const [result, setResult] = useState<MaterialPriceResult | null>(null)
  const [loading, setLoading] = useState(false)

  const lookup = useCallback(
    async (
      productLine: string,
      productBase?: string,
      materialCode?: string,
      supplierSlug?: string,
    ) => {
      if (!productLine || !claimUuid) return
      setLoading(true)
      try {
        const res = await api.post('/claims/pricing/material-lookup/', {
          claim_uuid: claimUuid,
          product_line: productLine,
          product_base: productBase ?? '',
          material_code: materialCode ?? '',
          supplier_slug: supplierSlug ?? '',
        })
        // The api client's response interceptor already unwraps the
        // `{status, message, result}` envelope into `res.data` (see
        // shared/lib/api-envelope.ts), so the payload is `res.data` - reading
        // `res.data.data` double-unwraps to undefined and silently drops the
        // resolved price, so the lookup never auto-fills.
        const data = (res.data ?? null) as MaterialPriceResult | null
        setResult(data)
        return data
      } catch {
        setResult(null)
        return null
      } finally {
        setLoading(false)
      }
    },
    [claimUuid],
  )

  return { lookup, result, loading, reset: () => setResult(null) }
}

export function useLaborRateLookup(claimUuid: string) {
  const [result, setResult] = useState<LaborRateResult | null>(null)
  const [loading, setLoading] = useState(false)

  const lookup = useCallback(
    // Product line is optional (WPS-161): a blank line resolves the
    // any-product-line rate, so only work type + claim are required.
    async (workType: string, productLine?: string | null) => {
      if (!workType || !claimUuid) return
      setLoading(true)
      try {
        const res = await api.post('/claims/pricing/labor-lookup/', {
          claim_uuid: claimUuid,
          work_type: workType,
          product_line: productLine ?? '',
        })
        // The response interceptor already unwrapped the envelope into
        // `res.data` (see the material-lookup note above); reading
        // `res.data.data` here returned undefined, which is why the labor rate
        // never pulled into the settlement line item (WPS-161).
        const data = (res.data ?? null) as LaborRateResult | null
        setResult(data)
        return data
      } catch {
        setResult(null)
        return null
      } finally {
        setLoading(false)
      }
    },
    [claimUuid],
  )

  return { lookup, result, loading, reset: () => setResult(null) }
}

export interface ProductUomResult {
  /** Blank when nothing publishes a unit for this product. */
  uom: string
  /** ``product_catalogue``, ``pricing_book`` or ``pricing_book_other_zone``. */
  uom_source: string
  /**
   * Whether a missing unit should BLOCK the line.
   *
   * False for a tenant that publishes no units at all - refusing there would
   * block every product rather than the unmapped ones, which is
   * indistinguishable from the feature being broken.
   */
  enforced: boolean
}

/**
 * Resolve a product's approved unit of measure (WPS-175 §7).
 *
 * Always returns a result when the call succeeds; read `uom` for the value and
 * `enforced` for whether a blank one is a reason to refuse the line. Returns
 * null only when the lookup itself failed, which must never block - a network
 * blip is not evidence that a product has no unit.
 */
export function useProductUomLookup(claimUuid: string) {
  const lookup = useCallback(
    async (
      productLine: string,
      productBase?: string | null,
      materialCode?: string | null,
    ) => {
      if (!claimUuid) return null
      try {
        const res = await api.post('/claims/pricing/uom-lookup/', {
          claim_uuid: claimUuid,
          product_line: productLine ?? '',
          product_base: productBase ?? '',
          material_code: materialCode ?? '',
        })
        // The api client already unwraps the `{status, message, result}`
        // envelope into `res.data` - see the material-lookup note above.
        return (res.data ?? null) as ProductUomResult | null
      } catch {
        return null
      }
    },
    [claimUuid],
  )

  return { lookup }
}

/**
 * What the Add Line Item card already knows about a claim (WPS-175 §7): the
 * product line(s) it may spend against, the quantity a new line should start
 * at, and the payee the claim recorded.
 */
export interface SettlementClaimContext {
  /** Empty means no restriction is configured - offer every product line. */
  allowed_product_lines: string[]
  default_quantity: string | null
  /** ``approved`` (the lab narrowed the claim) or ``registration``. */
  default_quantity_source: string
  payee_defaults: Record<string, { payee?: string; address?: unknown }>
}

export function useSettlementClaimContext(claimUuid: string | null) {
  return useQuery<SettlementClaimContext>({
    queryKey: ['settlement-claim-context', claimUuid],
    queryFn: () =>
      api.get(`/claims/claims/${claimUuid}/settlement/context/`).then((r) => r.data),
    enabled: !!claimUuid,
    staleTime: 60 * 1000,
  })
}

// ── LOA Mutations ───────────────────────────────────────
//
// PR #389. These mutations call backend endpoints
// (``/loa/start/`` etc.) that the LOA Approval Flow PR ships
// frontend-only. The backend endpoints are introduced separately
// and tracked under the LOA assimilation work - until they land,
// the LOA panel renders but the action buttons 404 against the
// generic engine.

export function useStartLOA(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { loa_type: LOAType }) =>
      api
        .post(`/claims/claims/${claimUuid}/loa/start/`, data)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

/**
 * Input for the LOA decision mutations below. ``rationale`` is the reviewer's
 * free-text decision note from the LOA panel textarea. ``captureFields`` are the
 * ClaimAttribute code_names the executing action actually declares in its
 * ``capture_fields`` (resolved by the caller from the live action metadata) -
 * the note is stored under each of them.
 */
export interface LOADecisionInput {
  rationale: string
  captureFields?: string[]
}

/**
 * Build the ``captured_values`` for an LOA decision action.
 *
 * The backend (``apply_captured_claim_values``) persists ONLY the captured keys
 * that appear in the action's ``capture_fields`` and silently drops the rest -
 * so a fixed ``{ rationale }`` key is never stored (no OC LOA action declares a
 * ``rationale`` field; they declare ``loa_decision`` and, for deny,
 * ``deny_reason``). Keying the note by the action's real capture fields is what
 * makes it persist, and reading those from the action metadata keeps this
 * tenant-agnostic - the ``fallback`` only covers the case where the caller could
 * not resolve the metadata.
 */
function loaDecisionCapturedValues(
  note: string,
  captureFields: string[] | undefined,
  fallback: string[],
): Record<string, string> {
  const codes = captureFields && captureFields.length ? captureFields : fallback
  const values: Record<string, string> = {}
  for (const code of codes) values[code] = note
  return values
}

export function useApproveLOA(claimUuid: string, actionKey = 'loa_approve') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: LOADecisionInput) =>
      api
        .post(`/claims/claims/${claimUuid}/workflow/action/execute/`, {
          action_key: actionKey,
          captured_values: loaDecisionCapturedValues(
            data.rationale,
            data.captureFields,
            ['loa_decision'],
          ),
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useRejectLOA(claimUuid: string, actionKey = 'loa_deny') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: LOADecisionInput) =>
      api
        .post(`/claims/claims/${claimUuid}/workflow/action/execute/`, {
          action_key: actionKey,
          captured_values: loaDecisionCapturedValues(
            data.rationale,
            data.captureFields,
            ['loa_decision', 'deny_reason'],
          ),
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useRequestChangesLOA(claimUuid: string, actionKey = 'loa_request_changes') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: LOADecisionInput) =>
      api
        .post(`/claims/claims/${claimUuid}/workflow/action/execute/`, {
          action_key: actionKey,
          captured_values: loaDecisionCapturedValues(
            data.rationale,
            data.captureFields,
            ['loa_decision'],
          ),
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

// Stops a payment that has not been paid. A paid one is voided instead
// (`useVoidPayment`), which keeps it on record and sends the claim back.
export function useCancelPayment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      paymentUuid,
      data,
    }: {
      paymentUuid: string
      data: { reason: string }
    }) =>
      api
        .post(
          `/claims/claims/${claimUuid}/payments/${paymentUuid}/cancel/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

/** WPS-243: void a paid payment so its settlement can be corrected and reissued. */
export function useVoidPayment(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentUuid, reason }: { paymentUuid: string; reason: string }) =>
      api
        .post(`/claims/claims/${claimUuid}/payments/${paymentUuid}/void/`, {
          reason,
        })
        .then((r) => r.data),
    // Refreshed in the background, so the dialog closes on the void itself
    // rather than after every claim query has refetched behind it.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

// ── Bulk settlement (generic, cross-claim) ───────────────

export interface BulkSettleItem {
  claim_uuid: string
  amount: string | number
  recipient_type?: string
  payee?: string
}

export interface BulkSettlePayload {
  payout_mode: 'combined' | 'per_claim'
  payment_type: string
  reference_number?: string
  payment_date?: string
  combined_on_claim_uuid?: string
  combined_recipient_type?: string
  combined_payee?: string
  settle_action_key?: string
  items: BulkSettleItem[]
}

export interface BulkSettleResult {
  payments_created: number
  total_amount: string
  claims_settled: number
  errors: { row: number; error: string }[]
}

/**
 * Settle multiple claims at once (generic platform capability). Reusable by
 * any parent/child claim grouping in any tenant - not HOA-specific.
 */
export function useBulkSettleClaims() {
  const qc = useQueryClient()
  return useMutation<BulkSettleResult, Error, BulkSettlePayload>({
    mutationFn: (data) =>
      api.post('/claims/bulk-settle/', data).then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      invalidateClaimsQueries(qc)
      qc.invalidateQueries({ queryKey: ['claim-children'] })
    },
  })
}
